import React, { useEffect, useId, useRef, useState } from 'react';
import logger, { LogCategory } from '@/lib/logging';
import { playPostureCountdownLockSound, playPostureCountdownTickSound } from './lib/capture-sounds';

interface CaptureCountdownProps {
  isActive: boolean;
  onComplete: () => void;
  onCancel: () => void;
  startCount?: number;
}

const CaptureCountdown: React.FC<CaptureCountdownProps> = ({
  isActive,
  onComplete,
  onCancel,
  startCount = 3
}) => {
  const reactId = useId();
  const instanceIdRef = useRef(`countdown-${reactId.replace(/:/g, '')}`);
  const [count, setCount] = useState(startCount);
  const onCompleteRef = useRef(onComplete);
  const onCancelRef = useRef(onCancel);
  const countRef = useRef(startCount);
  const startedAtRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    countRef.current = count;
  }, [count]);

  useEffect(() => {
    logger.debug('PostureCaptureCountdown: mounted', {
      category: LogCategory.UI,
      countdown: true,
      instanceId: instanceIdRef.current,
      startCount,
    });
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      logger.debug('PostureCaptureCountdown: unmounted', {
        category: LogCategory.UI,
        countdown: true,
        instanceId: instanceIdRef.current,
      });
    };
  }, []);

  useEffect(() => {
    if (!isActive) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      startedAtRef.current = null;
      if (countRef.current !== startCount) {
        logger.debug('PostureCaptureCountdown: reset while inactive', {
          category: LogCategory.UI,
          countdown: true,
          instanceId: instanceIdRef.current,
          previousCount: countRef.current,
          resetTo: startCount,
        });
      }
      setCount(startCount);
      return;
    }

    // Fresh activation: latch start time once and drive countdown via interval so
    // parent re-renders do not reschedule/cancel one-shot timers.
    startedAtRef.current = Date.now();
    setCount(startCount);
    logger.info('PostureCaptureCountdown: activated', {
      category: LogCategory.UI,
      countdown: true,
      instanceId: instanceIdRef.current,
      startCount,
    });
    playPostureCountdownLockSound();

    // For a 1s countdown there is no 3→2 style transition; play a distinct step tick shortly after lock.
    let delayedOneSecondTickTimer: number | null = null;
    if (startCount === 1) {
      delayedOneSecondTickTimer = window.setTimeout(() => {
        delayedOneSecondTickTimer = null;
        playPostureCountdownTickSound(1);
      }, 200);
    }

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    intervalRef.current = window.setInterval(() => {
      const startedAt = startedAtRef.current;
      if (!startedAt) return;

      const elapsedMs = Date.now() - startedAt;
      const next = Math.max(startCount - Math.floor(elapsedMs / 1000), 0);

      if (next !== countRef.current) {
        const from = countRef.current;
        logger.debug('PostureCaptureCountdown: tick', {
          category: LogCategory.UI,
          countdown: true,
          instanceId: instanceIdRef.current,
          from,
          to: next,
          elapsedMs,
        });
        if (next < from && next >= 1) {
          playPostureCountdownTickSound(next);
        }
        countRef.current = next;
        setCount(next);
      }

      if (next <= 0) {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        startedAtRef.current = null;
        logger.info('PostureCaptureCountdown: completed', {
          category: LogCategory.UI,
          countdown: true,
          instanceId: instanceIdRef.current,
        });
        onCompleteRef.current();
      }
    }, 200);

    return () => {
      if (delayedOneSecondTickTimer !== null) {
        window.clearTimeout(delayedOneSecondTickTimer);
        delayedOneSecondTickTimer = null;
      }
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, startCount]);

  if (!isActive || count === 0) return null;

  return (
    <div 
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm cursor-pointer"
      onClick={() => {
        logger.info('PostureCaptureCountdown: cancel tapped', {
          category: LogCategory.UI,
          countdown: true,
          instanceId: instanceIdRef.current,
          count: countRef.current,
        });
        onCancelRef.current();
      }}
    >
      <div className="text-9xl font-bold text-white animate-pulse drop-shadow-[0_0_15px_rgba(0,0,0,0.5)]">
        {count}
      </div>
      <div className="absolute bottom-20 text-white/80 text-sm uppercase tracking-widest">
        Tap to Cancel
      </div>
    </div>
  );
};

export default CaptureCountdown;

